import React from 'react';
import { CheckIcon } from '@heroicons/react/24/solid';
import { Subscription, BillingCycle } from '../services/api';

interface SubscriptionPreviewProps {
  subscription: Subscription;
}

const SubscriptionPreview: React.FC<SubscriptionPreviewProps> = ({ subscription }) => {
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatFeatureValue = (featureCode: string, value: string): string => {
    const valueLower = value.toLowerCase();

    // Checkbox-only features (INSIGHT, IMOLA_MEMORY)
    if (featureCode === 'INSIGHT' || featureCode === 'IMOLA_MEMORY') {
      if (valueLower === 'unlimited' || valueLower === 'true') {
        return 'enabled'; // Will show checkmark
      }
      return 'disabled';
    }

    // Number + unlimited checkbox (PATHSIGHT, FORESIGHT, CONVO)
    if (
      featureCode === 'PATHSIGHT' ||
      featureCode === 'FORESIGHT' ||
      featureCode === 'CONVO'
    ) {
      if (valueLower === 'unlimited') {
        return 'unlimited';
      }
      const num = parseInt(value);
      if (isNaN(num) || num === 0) {
        return '0';
      }
      return `${num}x/bln`;
    }

    // COMPETITIVENESS_DATA
    if (featureCode === 'COMPETITIVENESS_DATA') {
      if (value === 'ALL_PLATFORM') {
        return 'All Platform';
      } else if (value === 'THREE_PLATFORM') {
        return '3 Platform';
      } else if (value === 'ONE_PLATFORM') {
        return '1 Platform';
      }
      return value;
    }

    // Number-only features (CATEGORY_STORE, MAX_CONNECT_STORE)
    return value;
  };

  const shouldHighlight = (featureCode: string, value: string): boolean => {
    const valueLower = value.toLowerCase();

    // Highlight enabled/unlimited features
    if (featureCode === 'INSIGHT' || featureCode === 'IMOLA_MEMORY') {
      return valueLower === 'unlimited' || valueLower === 'true';
    }

    if (
      featureCode === 'PATHSIGHT' ||
      featureCode === 'FORESIGHT' ||
      featureCode === 'CONVO'
    ) {
      if (valueLower === 'unlimited') return true;
      const num = parseInt(value);
      return !isNaN(num) && num > 0;
    }

    if (featureCode === 'COMPETITIVENESS_DATA') {
      return value === 'ALL_PLATFORM' || value === 'THREE_PLATFORM';
    }

    if (featureCode === 'MAX_CONNECT_STORE') {
      const num = parseInt(value);
      return !isNaN(num) && num > 1;
    }

    return false;
  };

  const getFeatureDisplayName = (featureCode: string): string => {
    switch (featureCode) {
      case 'CATEGORY_STORE':
        return 'Category Store';
      case 'MAX_CONNECT_STORE':
        return 'Max. Connect Store';
      case 'INSIGHT':
        return 'Insight';
      case 'PATHSIGHT':
        return 'Pathsight';
      case 'FORESIGHT':
        return 'Foresight';
      case 'CONVO':
        return 'Convo';
      case 'COMPETITIVENESS_DATA':
        return 'Competitiveness Data';
      case 'IMOLA_MEMORY':
        return 'IMOLA Memory';
      default:
        return featureCode;
    }
  };

  const billingLabel = subscription.productBillingCycle === BillingCycle.MONTHLY ? 'bln' : 'thn';

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 px-4 py-3">
        <h3 className="text-lg font-bold text-white">{subscription.productName}</h3>
        <p className="text-sm text-green-100 mt-1">
          {formatPrice(subscription.currentPrice)} / {billingLabel}
        </p>
      </div>

      {/* Features List */}
      <div className="divide-y divide-gray-100">
        {subscription.features && subscription.features.length > 0 ? (
          subscription.features.map((feature, index) => {
            const featureCode = feature.featureCode;
            const displayValue = formatFeatureValue(featureCode, feature.value);
            const isHighlighted = shouldHighlight(featureCode, feature.value);
            const isCheckmark = (featureCode === 'INSIGHT' || featureCode === 'IMOLA_MEMORY') &&
              (displayValue === 'enabled');
            const isDisabled = (featureCode === 'INSIGHT' || featureCode === 'IMOLA_MEMORY') &&
              displayValue === 'disabled';

            return (
              <div
                key={feature.id || index}
                className={`px-4 py-3 flex items-center justify-between ${isHighlighted ? 'bg-green-50' : 'bg-white'
                  }`}
              >
                <span className="text-sm font-medium text-gray-900">
                  {getFeatureDisplayName(featureCode)}
                </span>
                <div className="flex items-center">
                  {isCheckmark ? (
                    <CheckIcon className="w-5 h-5 text-green-600" />
                  ) : isDisabled ? (
                    <span className="text-gray-400">—</span>
                  ) : displayValue === 'unlimited' ? (
                    <span className="text-sm font-semibold text-green-700">Unlimited</span>
                  ) : (
                    <span className={`text-sm font-medium ${isHighlighted ? 'text-green-700' : 'text-gray-700'
                      }`}>
                      {displayValue}
                    </span>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="px-4 py-8 text-center text-gray-500 text-sm">
            No features configured
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionPreview;

